import type { WebsiteFontId, WebsiteInterfaceLocale } from '../../types/website-templates';

export interface WebsiteFontDefinition {
  readonly id: WebsiteFontId;
  readonly family: string;
  readonly style: 'serif' | 'sans';
  readonly covers: {
    readonly arabic: boolean;
    readonly hebrew: boolean;
    readonly latin: boolean;
  };
}

/** Closed allowlist of self-hosted families. No external font URLs exist. */
export const WEBSITE_FONTS: Readonly<Record<WebsiteFontId, WebsiteFontDefinition>> = {
  'ibm-plex-sans-arabic': {
    id: 'ibm-plex-sans-arabic',
    family: 'IBM Plex Sans Arabic',
    style: 'sans',
    covers: { arabic: true, hebrew: false, latin: true },
  },
  amiri: {
    id: 'amiri',
    family: 'Amiri',
    style: 'serif',
    covers: { arabic: true, hebrew: false, latin: true },
  },
  'noto-sans-hebrew': {
    id: 'noto-sans-hebrew',
    family: 'Noto Sans Hebrew',
    style: 'sans',
    covers: { arabic: false, hebrew: true, latin: true },
  },
  'frank-ruhl-libre': {
    id: 'frank-ruhl-libre',
    family: 'Frank Ruhl Libre',
    style: 'serif',
    covers: { arabic: false, hebrew: true, latin: true },
  },
  inter: {
    id: 'inter',
    family: 'Inter',
    style: 'sans',
    covers: { arabic: false, hebrew: false, latin: true },
  },
  fraunces: {
    id: 'fraunces',
    family: 'Fraunces',
    style: 'serif',
    covers: { arabic: false, hebrew: false, latin: true },
  },
};

const SCRIPT_FALLBACKS: Readonly<
  Record<'serif' | 'sans', Readonly<Record<'arabic' | 'hebrew' | 'latin', WebsiteFontId>>>
> = {
  serif: { arabic: 'amiri', hebrew: 'frank-ruhl-libre', latin: 'fraunces' },
  sans: { arabic: 'ibm-plex-sans-arabic', hebrew: 'noto-sans-hebrew', latin: 'inter' },
};

const LOCALE_SCRIPT_ORDER: Readonly<
  Record<WebsiteInterfaceLocale, readonly ('arabic' | 'hebrew' | 'latin')[]>
> = {
  ar: ['arabic', 'latin', 'hebrew'],
  he: ['hebrew', 'latin', 'arabic'],
  en: ['latin', 'arabic', 'hebrew'],
};

export function isWebsiteFontId(value: unknown): value is WebsiteFontId {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(WEBSITE_FONTS, value);
}

/**
 * CSS font-family stack: the chosen family first, then same-style families so
 * Arabic, Hebrew and Latin text all resolve to a self-hosted face. The store
 * locale only orders the fallbacks.
 */
export function fontStackFor(id: WebsiteFontId, locale: WebsiteInterfaceLocale): string {
  const chosen = WEBSITE_FONTS[id];
  const families: string[] = [chosen.family];
  for (const script of LOCALE_SCRIPT_ORDER[locale]) {
    const fallback = WEBSITE_FONTS[SCRIPT_FALLBACKS[chosen.style][script]];
    if (!families.includes(fallback.family)) families.push(fallback.family);
  }
  return [
    ...families.map((family) => `'${family}'`),
    chosen.style === 'serif' ? 'serif' : 'sans-serif',
  ].join(', ');
}
